// -----------------------------------------
// Creates customizable alert, prompt, and confirmation dialogs. Used instead of browser defaults.
// -----------------------------------------
type DialogConfig = {
    message?: string;
    trueText?: string;
    falseText?: string;
    defaultValue?: string;
    type?: "confirm" | "prompt" | "alert"; // defaults to confirm
}

export default class Dialog {
    message: string; trueText: string; falseText:string; defaultValue: string;
    dialog: any; backdrop: any; textInput: any; parent: any; trueButton: any; 
    falseButton: any; type: "confirm" | "prompt" | "alert";
    constructor(config: DialogConfig){
        let isConfirm: boolean = config.type ? config.type === "confirm" : true;
        this.type = config.type || "confirm"; // type of alert confirm and prompt are available
        this.message = config.message ? config.message : isConfirm ? "Are you sure you want to leave? Any unsaved changes could be lost." : "Please input below."; // Dialog message
        this.trueText = config.trueText ? config.trueText : isConfirm ? "Okay" : "Submit"; // Text for submit/true button
        this.falseText = config.falseText || "Cancel"; // Text for cancel/false button
        this.defaultValue = config.defaultValue || ""; // Default value for prompt
        this.parent = document.body; // <body> tag in index.html
        this.backdrop = undefined; // Black semi-transparent background
        this.dialog = undefined; // Wrapper around inputs/button
        this.textInput = undefined; // Only present on prompt inputs
        this.trueButton = undefined; // Submit button
        this.falseButton = undefined; // Cancel button
        this._createDialog();
        this._appendDialog();
    }

    _createDialog = () => { // Sets up dialog layout
        // Creates 100vh, 100vw black backdrop
        this.backdrop = document.createElement("div");
        this.backdrop.classList.add("confirm-dialog-backdrop");
        // Adds confirm dialog as a child to backdrop
        this.dialog = document.createElement("div");
        this.dialog.classList.add(`${this.type}-dialog`);
        this.dialog.id = "global-dialog";
        this.backdrop.appendChild(this.dialog);
        // Adds Message as child to dialog
        const message = document.createElement("div");
        message.innerHTML = this.message;
        message.classList.add("text-secondary");
        this.dialog.appendChild(message);
        if(this.type === "prompt"){ // If  type is prompt place text input below
            // Adding prompt input
            this.textInput = document.createElement("input");
            this.textInput.classList.add("form-control");
            this.textInput.id = "prompt-text-input";
            if(this.defaultValue){ this.textInput.value = this.defaultValue }
            this.dialog.appendChild(this.textInput);
        }
        // Adds ButtonGroup to dialog
        const buttonGroup = document.createElement("div");
        buttonGroup.classList.add(`${this.type}-dialog-button-group`);
        this.dialog.appendChild(buttonGroup);
        // Places in buttons in buttonGroup
        this.falseButton = document.createElement("button");
        this.falseButton.classList.add(
            "confirm-dialog-button",
            "confirm-dialog-button--false",
            "btn", "btn-outline-danger"
        );
        this.falseButton.type = "button";
        this.falseButton.textContent = this.falseText;
        buttonGroup.appendChild(this.falseButton);
        this.trueButton = document.createElement("button");
        this.trueButton.classList.add(
            "confirm-dialog-button",
            "confirm-dialog-button--true",
            "btn", "btn-outline-primary"
        );
        this.trueButton.type = "button";
        this.trueButton.textContent = this.trueText;
        buttonGroup.appendChild(this.trueButton);
    }

    _appendDialog() { // Places the dialog on the body of document
        this.dialog.classList.add("global-dialog-in"); // mounted animation
        this.parent.appendChild(this.backdrop);
    }

    _destroy() { this.parent.removeChild(this.backdrop); } // Removes backdrop and children from body tag

    exit = () => { // Shows exit animation and calls this._destroy() after playing
        let dialog: any = document.getElementById("global-dialog");
        if(dialog){
            dialog.classList.add("global-dialog-out"); // adding close animation class
            setTimeout(() => {
                dialog.classList.add("d-none");
                this._destroy();
            }, 300) // 300 is the length of the close animation
        }else{ this._destroy() }
    }

    show = () => { // Sets up promise and event listeners for dialog, depending on which type
        if(this.type === "confirm"){
            return new Promise((resolve: any, reject: any) => {
                // If dialog is not being shown reject;
                if(!this.dialog || !this.trueButton || !this.falseButton){
                    reject("Modal wasn't created properly");
                }
                // Adds click listeners to confirm buttons and resolves promise
                this.trueButton.addEventListener("click", () => { resolve(true); this.exit(); });
                this.falseButton.addEventListener("click", () => { resolve(false); this.exit(); });
            });
        }
        else if(this.type === "alert"){
            return new Promise((resolve: any, reject: any) => {
                if(!this.dialog || !this.trueButton || this.falseButton){
                    reject("Modal wasn't created properly");
                }
                this.trueButton.addEventListener("click", () => { resolve(true); this.exit(); });
                this.falseButton.classList.add("d-none");
            })
        }
        else{
            document.getElementById("prompt-text-input")?.focus();
            return new Promise((resolve: any, reject: any) => {
                // If dialog is not being shown reject;
                if(!this.dialog || !this.trueButton || !this.falseButton){
                    reject("Modal wasn't created properly");
                }
                // Adds click listeners to confirm buttons and resolves promise
                this.trueButton.addEventListener("click", () => { 
                    // Find text input
                    let value: any = document.getElementById("prompt-text-input");
                    if(value){
                        // If input has a value return value in input
                        resolve(value.value); 
                        this.exit(); 
                    }else{
                        reject("Text Input not found");
                    }
                });
                // Sends an empty string if cancel is clicked
                this.falseButton.addEventListener("click", () => { resolve(""); this.exit(); });
            });
        }
    }
}

// const demoDialogs = async () => {
//     let p: any = new PromptDialog({});
//     let value: string = await p.prompt();
//     //console.log("Value for Prompt : ", value); 
//     let c: any = new ConfirmDialog({});
//     let answer: boolean = await c.confirm();
//     //console.log("Value for Confirm : ", answer); 
// }